import { useState } from "react"
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import { ArrowRight, ArrowUpRight, Check, Lock, ShieldCheck } from "lucide-react"
import { toast } from "sonner"
import { PERMISSION_LABELS } from "@vienna/contracts"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { PageHeading } from "@/components/app/page-heading"
import { api, ApiError } from "@/lib/api"
import { initials } from "@/lib/format"
import { conversationAutomationsQuery, conversationQuery } from "@/lib/queries"
import { useMe, useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/enrol/$conversationId")({
  loader: ({ context, params }) => Promise.all([context.queryClient.ensureQueryData(conversationQuery(params.workspace, params.conversationId)), context.queryClient.ensureQueryData(conversationAutomationsQuery(params.workspace, params.conversationId))]),
  component: Enrol,
})

/** Screen 53. */
function Enrol() {
  const { workspace } = useWorkspaceContext()
  const me = useMe()
  const { conversationId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const detail = useSuspenseQuery(conversationQuery(workspace.slug, conversationId)).data
  const automations = useSuspenseQuery(conversationAutomationsQuery(workspace.slug, conversationId)).data
  const { contact, conversation } = detail
  const [automationId, setAutomationId] = useState(automations[0]?.id ?? "")
  const [checked, setChecked] = useState(false)
  const params = { workspace: workspace.slug }
  const windowOpen = contact.supportWindowEndsAt ? new Date(contact.supportWindowEndsAt).getTime() > new Date(conversation.updatedAt).getTime() : false
  const chosen = automations.find((a) => a.id === automationId)
  const enrol = useMutation({
    mutationFn: () => api<{ runId: string }>(`/w/${workspace.slug}/conversations/${conversationId}/enrol`, { method: "POST", body: JSON.stringify({ automationId, confirm: true }) }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["workspace", workspace.slug] })
      toast.success(`${contact.displayName} enrolled in ${chosen?.name ?? "the automation"}`)
      await navigate({ to: "/w/$workspace/inbox", params, search: { c: conversationId } })
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.problem.title : "Could not enrol the contact"),
  })
  const checks: Array<[string, string, boolean]> = [
    ["Published version", chosen ? `Ready · v${chosen.liveVersion}` : "Choose an automation", Boolean(chosen)],
    ["Channel eligibility", windowOpen ? "Eligible now" : "Support window closed · rechecked at send", windowOpen],
    ["Duplicate active run", detail.activeRun ? `Active: ${detail.activeRun.automationName}` : "None found", !detail.activeRun],
    ["Human ownership", conversation.controlMode === "human" ? "Your team owns it · enrolling hands control to the journey" : "Free to start", true],
    ["Message purpose", contact.marketing === "granted" ? "Support and marketing" : "Support only · marketing steps stay blocked", true],
  ]
  return (
    <div className="page-wrap">
      <PageHeading title="Enrol in an automation" description="Choose a published journey and review eligibility before starting." />
      <div className="handover-layout">
        <Card>
          <CardHeader>
            <CardTitle>Contact to enrol</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center gap-3">
              <Avatar className="size-11">
                <AvatarFallback className="text-[11px] bg-stone-200 text-stone-700">{initials(contact.displayName)}</AvatarFallback>
              </Avatar>
              <div className="text-sm font-medium">
                {contact.displayName}
                <p className="mt-1 text-xs font-normal text-muted-foreground">
                  {contact.handle ?? "Verified identity"} · {conversation.provider === "instagram" ? "Instagram" : "Messenger"}
                </p>
              </div>
            </div>
            <div className="soft-panel text-xs leading-5">
              <strong>{detail.activeRun ? `Active run: ${detail.activeRun.automationName}` : "No active or paused run"}</strong>
              <p className="mt-2 text-muted-foreground">{detail.activeRun ? "Enrolling again is blocked while a run is active." : "This conversation is not in a journey."}</p>
            </div>
            <div className="context-line">
              <span>Support replies</span>
              <Badge variant="secondary">{windowOpen ? "Available" : "Window closed"}</Badge>
            </div>
            <div className="context-line">
              <span>Marketing consent</span>
              <span>{contact.marketing === "unknown" ? "Not recorded" : PERMISSION_LABELS[contact.marketing]}</span>
            </div>
            <div className="core-hint">
              <ShieldCheck className="mt-0.5 size-4 shrink-0" />
              <div>Enrolment does not create marketing permission. Marketing steps stay blocked.</div>
            </div>
            <Button variant="outline" className="w-full" asChild>
              <Link to="/w/$workspace/contacts/$contactId" params={{ ...params, contactId: contact.id }}>
                View contact profile
                <ArrowUpRight />
              </Link>
            </Button>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Choose a published automation</CardTitle>
            <CardDescription className="text-xs">Runs begin at the configured entry point.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {automations.length === 0 ? (
              <div className="soft-panel text-sm">
                <div className="flex items-center gap-2 font-medium">
                  <Lock className="size-4" /> No published automations for this channel
                </div>
                <p className="mt-2 text-xs leading-5 text-muted-foreground">Publish a journey in Automations to enrol contacts here.</p>
              </div>
            ) : (
              <Select value={automationId} onValueChange={setAutomationId}>
                <SelectTrigger className="w-full" aria-label="Automation">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {automations.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name} · Published v{a.liveVersion}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <div className="eligibility-checks">
              {checks.map(([label, result, ok]) => (
                <div key={label}>
                  <span>{label}</span>
                  <span className="flex items-center gap-2">
                    {result}
                    {ok ? <Check className="size-3.5 text-emerald-600" /> : <Lock className="size-3.5" />}
                  </span>
                </div>
              ))}
            </div>
            <label className="flex items-start gap-3 text-sm">
              <Checkbox checked={checked} onCheckedChange={(v) => setChecked(!!v)} />
              <span>
                Let this automation handle the conversation.
                <small className="mt-1 block text-muted-foreground">{me.name} is recorded as the actor.</small>
              </span>
            </label>
            <div className="flex justify-between border-t pt-5">
              <Button variant="outline" asChild>
                <Link to="/w/$workspace/inbox" params={params} search={{ c: conversationId }}>
                  Cancel
                </Link>
              </Button>
              <Button disabled={!checked || !chosen || Boolean(detail.activeRun) || enrol.isPending} onClick={() => enrol.mutate()}>
                Confirm enrolment
                <ArrowRight />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
